import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import {
  GetIntegrationsSummaryDocument,
  Integration,
  LoadIntegrationsDocument,
  SetIntegrationMutationVariables,
  useLoadIntegrationsQuery,
  useSetIntegrationMutation,
} from "~/operations";
import { ChatIntegrationName } from "../utils/chatOpsTypes";
import { useCanSetIntegration } from "~/lib/iam/actions/mondoo/captain/Captain/SetIntegration";
import { Path, PathValue, UseFormSetValue } from "react-hook-form";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";

export type UseChatOpsProps = {
  scope: SpaceScope | OrganizationScope;
  view: ChatIntegrationName;
};

export function useChatOps({ scope, view }: UseChatOpsProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { data } = useLoadIntegrationsQuery({
    variables: { mrn: scope.mrn },
  });

  const [setIntegrationMutation] = useSetIntegrationMutation({
    refetchQueries: [LoadIntegrationsDocument, GetIntegrationsSummaryDocument],
  });

  const [integration, setIntegration] = useState<Integration | null>(null);

  useEffect(() => {
    if (data) {
      setIntegration(data.integrations.find((i) => i?.name === view) ?? null);
    }
  }, [data]);

  const hasChatOpsPermission = useCanSetIntegration();

  const saveIntegration = async (
    variables: SetIntegrationMutationVariables,
  ) => {
    try {
      await setIntegrationMutation({
        variables,
      });
      enqueueSnackbar("Successfully saved Chat Ops Integration", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar("Failed to save Chat Ops Integration", {
        variant: "error",
      });
    }
  };

  // Shared function to handle URL change effect
  const handleUrlChange = <T extends { activeState: boolean }>(
    url: string,
    activeState: boolean,
    setValue: UseFormSetValue<T>,
  ) => {
    if (url && !activeState) {
      setValue("activeState" as Path<T>, true as PathValue<T, Path<T>>);
    }

    if (!url) {
      setValue("activeState" as Path<T>, false as PathValue<T, Path<T>>);
    }
  };

  return {
    integration,
    saveIntegration,
    hasChatOpsPermission,
    handleUrlChange,
  };
}
